import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Search, RefreshCw, Mail, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { useAuth } from '../../context/AuthContext';
import NewsletterModal from './NewsletterModal';

interface Newsletter {
  id: number;
  subject: string;
  from: string;
  text?: string;
  html?: string;
  message_id?: string;
  received_at?: string;
  created_at: string;
  status: string;
}

const NewslettersView: React.FC = () => {
  const { user } = useAuth();
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentNewsletter, setCurrentNewsletter] = useState<Newsletter | null>(null);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null);

  const fetchNewsletters = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('outsideNewsletters')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setNewsletters(data || []);
    } catch (error: unknown) {
      reportUiError('outsideNewsletters', 'list', error);
      toast.error(`Error fetching newsletters: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setNewsletters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsletters();
  }, []);

  // Deep link from Slack / webhooks: /newsletters?id=123
  useEffect(() => {
    if (loading || newsletters.length === 0) return;
    const openId = new URLSearchParams(window.location.search).get('id')?.trim();
    if (!openId) return;
    const match = newsletters.find((n) => String(n.id) === openId);
    if (match) {
      setCurrentNewsletter(match);
      setIsModalOpen(true);
    }
  }, [loading, newsletters]);

  const filteredAndSortedNewsletters = newsletters.filter(newsletter => {
    const searchLower = searchQuery.toLowerCase();
    return (
      newsletter.subject?.toLowerCase().includes(searchLower) ||
      newsletter.from?.toLowerCase().includes(searchLower) ||
      newsletter.text?.toLowerCase().includes(searchLower)
    );
  }).sort((a, b) => {
    if (!sortConfig) return 0;

    const aValue = a[sortConfig.column as keyof Newsletter];
    const bValue = b[sortConfig.column as keyof Newsletter];

    if (aValue === null) return sortConfig.direction === 'asc' ? -1 : 1;
    if (bValue === null) return sortConfig.direction === 'asc' ? 1 : -1;

    return sortConfig.direction === 'asc'
      ? aValue > bValue ? 1 : -1
      : aValue < bValue ? 1 : -1;
  });

  const handleSort = (column: string) => {
    setSortConfig(current => {
      if (current?.column === column) {
        return current.direction === 'asc'
          ? { column, direction: 'desc' }
          : null;
      }
      return { column, direction: 'asc' };
    });
  };

  const handleAddNewsletter = () => {
    setCurrentNewsletter(null);
    setIsModalOpen(true);
  };

  const handleEditNewsletter = (newsletter: Newsletter) => {
    setCurrentNewsletter(newsletter);
    setIsModalOpen(true);
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Mail className="w-8 h-8 mr-3 text-emerald-500" />
            Reference Materials
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Reading list and reference content from external sources
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search newsletters..."
              className="pl-10 pr-4 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={handleAddNewsletter}
            className="flex items-center px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Newsletter
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {['Subject', 'From', 'Status', 'Date'].map((header) => (
                  <th
                    key={header}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort(header.toLowerCase())}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{header}</span>
                      {sortConfig?.column === header.toLowerCase() && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-emerald-500 mx-auto" />
                  </td>
                </tr>
              ) : filteredAndSortedNewsletters.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No newsletters found
                  </td>
                </tr>
              ) : (
                filteredAndSortedNewsletters.map((newsletter) => (
                  <tr
                    key={newsletter.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => handleEditNewsletter(newsletter)}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {newsletter.subject || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {newsletter.from || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {newsletter.status ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                            {newsletter.status}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(newsletter.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditNewsletter(newsletter);
                        }}
                        className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <NewsletterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        newsletter={currentNewsletter}
        onSuccess={fetchNewsletters}
      />
    </div>
  );
};

export default NewslettersView;
